<template>
  <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 p-6 mb-4">
    <!-- 标题行 -->
    <div class="flex justify-between items-start mb-4">
      <div class="flex items-center gap-3 flex-1">
        <!-- 优先级标签 -->
        <span :class="getPriorityBadgeClass(message.priority)" class="px-2 py-1 text-xs font-medium rounded">
          {{ MessagePriorityLabel[message.priority] }}
        </span>
        <!-- 消息类型标签 -->
        <span v-if="message.type === MessageType.SYSTEM_NOTIFICATION"
          class="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
          📢 系统通知
        </span>
        <span v-else
          class="px-2 py-1 text-xs font-medium rounded bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
          💬 私信
        </span>
        <!-- 标题 -->
        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {{ message.title }}
        </h3>
      </div>
      <span class="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap ml-4">
        {{ formatDate(message.createdAt) }}
      </span>
    </div>



    <!-- 内容预览 -->
    <div class="bg-gray-50 dark:bg-gray-700 rounded p-3 mb-4">
      <p class="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
        {{ getContentPreview(message.content) }}
      </p>
    </div>

    <!-- 操作按钮 -->
    <div class="flex gap-2">
      <button @click="handleView"
        class="cancel-btn text-sm font-medium">
        查看详情
      </button>
      <button @click="handleEdit"
        class="primary-btn text-sm font-medium">
        编辑
      </button>
      <button @click="handleDelete"
        class="danger-btn text-sm font-medium">
        删除
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { MessagePriorityLabel, MessagePriority, MessageType } from '@/types/message'
import type { Message } from '@/types/message'
import { formatDate } from '@/utils/date'

/**
 * 组件属性
 */
interface Props {
  /** 消息数据 */
  message: Message
}

/**
 * 组件事件
 */
interface Emits {
  /** 查看详情事件 */
  (e: 'view', messageId: number): void
  /** 编辑事件 */
  (e: 'edit', messageId: number): void
  /** 删除事件 */
  (e: 'delete', messageId: number): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

/**
 * 获取优先级徽章样式类
 */
const getPriorityBadgeClass = (priority: MessagePriority): string => {
  const classMap = {
    [MessagePriority.HIGH]: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    [MessagePriority.NORMAL]: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    [MessagePriority.LOW]: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  }
  return classMap[priority] || classMap[MessagePriority.NORMAL]
}

/**
 * 获取内容预览 (去除 HTML 标签, 截取前 100 字符)
 */
const getContentPreview = (content: string): string => {
  const text = content.replace(/<[^>]*>/g, '')
  return text.length > 100 ? text.substring(0, 100) + '...' : text
}

/**
 * 查看详情
 */
const handleView = () => {
  emit('view', props.message.id)
}

/**
 * 编辑
 */
const handleEdit = () => {
  emit('edit', props.message.id)
}

/**
 * 删除
 */
const handleDelete = () => {
  emit('delete', props.message.id)
}
</script>
